// components/ArtistChecklist.tsx
import { m } from "framer-motion";
import { useCommissionStore } from "../stores/commissionStore";
import { useArtistOptions } from "../hooks/useArtistOptions";
import { computeChecklist } from "../lib/checklist";
import { useIntakeCopy } from "../i18n/useIntakeCopy";
import { DrawCheck, ProgressRing } from "../motion/primitives";
import type { IntakeArtist } from "../types";
import { cn } from "@/lib/utils";

export function useChecklist(artist: IntakeArtist) {
  const draft = useCommissionStore();
  const opts = useArtistOptions(artist)!;
  const items = computeChecklist(draft, { ratingOptions: opts.ratingOptions });
  const required = items.filter((i) => i.required);
  return { items, done: required.filter((i) => i.done).length, total: required.length };
}

export function ArtistChecklist({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const goTo = useCommissionStore((s) => s.goTo);
  const { items, done, total } = useChecklist(artist);
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
      <div className="mb-3 flex items-center gap-3">
        <ProgressRing value={done / total} />
        <div>
          <h3 className="font-(family-name:--font-display) font-normal text-xl text-[var(--color-text)]">{fmt(c.checklist.title, { artist: artist.name })}</h3>
          <p className="text-xs text-[var(--color-text-muted)]">{fmt(c.checklist.progress, { done, total })}</p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i.id}>
            <button type="button" onClick={() => goTo(i.step)}
              className={cn("flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm hover:bg-[var(--color-bg)]",
                i.done ? "text-[var(--color-text)]" : i.required ? "text-[var(--color-text-muted)]" : "text-[var(--color-text-secondary)]")}>
              <DrawCheck done={i.done} />
              <m.span animate={{ opacity: i.done ? 1 : 0.85 }}>{c.checklist.items[i.id]}</m.span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
